import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Checkbox, Collapse, Divider, FormControlLabel, Grid, IconButton, Typography } from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import { IconResetSmall } from "@mzc-cloudplex/icons";
import useSearchFilter from "@hooks/useSearchFilter";
import { useSelector } from "react-redux";
import { getContentSchemasAPI } from "@modules/apis/content-schemas";
import { listAssetExtensionsAPI } from "@modules/apis/assets";
import { SEARCH_FILTER_TYPE, TABLE_LIST_TYPE } from "@constants";
import SearchFilters from "@components_v2/search-filters/search-filters";
import VersionedComponent from "@components/VersionedComponent";
import { VERSION_CM_12923_LIST_SEARCHBAR_FILTER } from "@cores/version";

const RESIZE_MENU_COUNT = 4;
const RESIZE_RESOLUTION = 500;

const AutocompleteTableListToolCollapse = ({
    type,
    isCollapsed,
    toolSize,
    numSelected,
    pageCount,
    buttons,
    pagination,
    sort,
    viewToggleButtons,
    hideCheckbox,
    handleClearCheckAll,
    handleChangeCheckAll,
    totalCount,
    totalView,
    onChangeSearchParams,
}) => {
    const { t } = useTranslation();
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const { getSearchFields, parseSearchParamsToSearchValue } = useSearchFilter(
        type,
        "list",
        SEARCH_FILTER_TYPE.FILTER,
    );

    const [resizedButtons, setResizedButtons] = useState([]);
    const [editTempValue, setEditTempValue] = useState({});
    const [filterOptionValues, setFilterOptionValues] = useState({});
    const [hideText, setHideText] = useState(false);
    const ref = useRef(null);

    const getFilterOptions = async (filterIds) => {
        try {
            const results = {};

            await Promise.all(
                filterIds.map(async (id) => {
                    switch (id) {
                        case "extensions":
                            let extensions = [];

                            const response = await listAssetExtensionsAPI(stage.id, stage.endpoint);
                            extensions = response.data;

                            results[id] = extensions.map(({ extension }) => ({
                                id: extension,
                                label: extension,
                                value: extension,
                            }));
                            break;

                        case "contentSchema":
                            let tempResults = [];
                            let nextToken = null;
                            do {
                                const params = { status: "ACTIVE", type: "EXTENDED_METADATA", nextToken };
                                const response = await getContentSchemasAPI(
                                    stage.id,
                                    stage.endpoint,
                                    project.id,
                                    params,
                                );
                                tempResults.push(...response.data.results);
                                nextToken = response.data.nextToken;
                            } while (!!nextToken);
                            results[id] = tempResults.map(({ id, name }) => ({
                                id,
                                label: name,
                                value: id,
                            }));
                            break;
                    }
                }),
            );
            setFilterOptionValues(results);
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        if (!buttons) return;

        if (parseInt(getComputedStyle(ref.current).width, 10) < RESIZE_RESOLUTION) {
            setResizedButtons(buttons(true));
            setHideText(true);
        } else {
            if (buttons(true).length > RESIZE_MENU_COUNT) {
                setResizedButtons(buttons(true));
            } else setResizedButtons(buttons(false));
            setHideText(false);
        }

        const resizeListener = () => {
            //사이즈가 변하는 위치에 있는 경우 resize 이벤트로 반복
            if (parseInt(getComputedStyle(ref.current).width, 10) < RESIZE_RESOLUTION) {
                setResizedButtons(buttons(true));
                setHideText(true);
            } else {
                if (buttons(true).length > RESIZE_MENU_COUNT) {
                    setResizedButtons(buttons(true));
                } else {
                    setResizedButtons(buttons(false));
                }
                setHideText(false);
            }
        };

        window.addEventListener("resize", resizeListener);
        return () => window.removeEventListener("resize", resizeListener);
    }, [buttons]);

    const initialSearchValue = useMemo(() => {
        if (type === TABLE_LIST_TYPE.ASSET) {
            return {
                creator: null,
                extensions: null,
                categoryIds: null,
                contentSchema: null,
                startAt: null,
                endAt: null,
            };
        } else return {};
    }, []);

    useEffect(() => {
        const searchValues = getSearchFields().reduce((acc, cur) => {
            return cur.attributions ? acc.concat(cur.attributions.filter((el) => el.essential)) : acc;
        }, []);
        let params = {};
        for (const s of searchValues) {
            params[s.id] = null;
        }
        const paramsForChips = parseSearchParamsToSearchValue(params);
        getFilterOptions(paramsForChips.map((el) => el.id));
        setEditTempValue(initialSearchValue);
    }, []);

    const onClickSubmit = (param) => {
        const params = { ...editTempValue, ...param };
        setEditTempValue(params);
        onChangeSearchParams(params);
    };

    return (
        <>
            <Box>
                <Grid container alignItems={`center`} justifyContent={`space-between`} py={2}>
                    {totalView && (
                        <Grid item flex={1}>
                            <Typography variant={"body1_700"}>{t("common::label::Total")}</Typography>
                            <Typography>{` ${totalCount}`}</Typography>
                        </Grid>
                    )}
                    <Grid item sx={{ display: "flex", alignItems: "center" }}>
                        {handleChangeCheckAll && (
                            <Grid item>
                                <Collapse in={numSelected === 0} orientation="horizontal">
                                    <Checkbox
                                        color={"greySecondary"}
                                        checked={numSelected > 1}
                                        disabled={!pageCount}
                                        indeterminate={numSelected > 0 && numSelected !== pageCount}
                                        onChange={handleChangeCheckAll}
                                        hidden={hideCheckbox}
                                    />
                                </Collapse>
                            </Grid>
                        )}
                        <VersionedComponent version={VERSION_CM_12923_LIST_SEARCHBAR_FILTER}>
                            <Grid container alignItems={`center`} gap={1.5}>
                                {sort && <Grid item>{sort}</Grid>}
                                <SearchFilters
                                    domain={type}
                                    type={SEARCH_FILTER_TYPE.FILTER}
                                    optionValues={filterOptionValues}
                                    values={editTempValue}
                                    onClickSubmit={onClickSubmit}
                                    onClickReset={() => onClickSubmit(initialSearchValue)}
                                />
                            </Grid>
                        </VersionedComponent>
                    </Grid>
                    <Grid item sx={{ display: "flex", flexDirection: "row", alignItems: "center", pr: 2 }}>
                        <Grid item>
                            <Grid container flexWrap={"nowrap"} alignItems={"center"}>
                                {pagination}
                            </Grid>
                        </Grid>
                        {viewToggleButtons}
                    </Grid>
                </Grid>
            </Box>
            <Collapse
                in={isCollapsed}
                collapsedSize={1}
                sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.common.white,
                    borderRadius: `3px`,
                    maxHeight: "64px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                }}
                ref={ref}
            >
                {isCollapsed && (
                    <Grid
                        container
                        flexDirection={toolSize === "sm" ? `column` : `row`}
                        alignItems={`center`}
                        justifyContent={`space-between`}
                        p={1}
                        sx={{
                            height: "64px",
                            overflow: "hidden",
                        }}
                    >
                        <Grid item xs>
                            <Grid container alignItems={"center"}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={"greySecondary"}
                                            checked={numSelected ? numSelected > 0 : false}
                                            indeterminate={numSelected > 0 && numSelected !== pageCount}
                                            hidden={hideCheckbox}
                                        />
                                    }
                                    label={
                                        <>
                                            <Typography
                                                variant={"body3_700"}
                                                color={theme.palette.secondary.contrastText}
                                            >
                                                {numSelected}
                                            </Typography>
                                            {!hideText && (
                                                <Typography
                                                    variant={"body3_400"}
                                                    color={theme.palette.secondary.contrastText}
                                                >
                                                    {` `}
                                                    {t("common::label::item selected", "item selected")}
                                                </Typography>
                                            )}
                                        </>
                                    }
                                    onClick={handleChangeCheckAll}
                                    sx={{ ml: 0 }}
                                />
                                <Divider flexItem orientation={"vertical"} sx={{ my: 0.5, mr: 1.5, opacity: 0.12 }} />
                                {hideText ? (
                                    <IconButton size={"xsmall"} onClick={handleClearCheckAll}>
                                        <IconResetSmall size={16} htmlColor={theme.palette.secondary.contrastText} />
                                    </IconButton>
                                ) : (
                                    <Button
                                        size={"small"}
                                        variant={"text"}
                                        endIcon={
                                            <IconResetSmall
                                                size={16}
                                                htmlColor={theme.palette.secondary.contrastText}
                                            />
                                        }
                                        onClick={handleClearCheckAll}
                                    >
                                        {!hideText ? (
                                            <Typography
                                                variant={"body1Regular"}
                                                color={theme.palette.secondary.contrastText}
                                            >
                                                Clear
                                            </Typography>
                                        ) : undefined}
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container alignItems={"flex-end"} gap={1}>
                                {resizedButtons.map((button, index) => (
                                    <Fragment key={`list-tools-buttons-${index}`}>{button}</Fragment>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Collapse>
        </>
    );
};

export default AutocompleteTableListToolCollapse;
